/**
 * components/procurement/DocumentsSection.jsx — Contract Documents repository.
 * Upload, download (presigned), and delete contract documents (S3-backed).
 */
import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, FileText, Download, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { uploadDocument, getDocumentDownloadUrl, deleteDocument } from '../../api/contracts';

const fmtSize = (b) => {
  if (b == null) return '';
  if (b >= 1e6) return `${(b / 1e6).toFixed(1)} MB`;
  if (b >= 1e3) return `${(b / 1e3).toFixed(0)} KB`;
  return `${b} B`;
};

export function DocumentsSection({ accountId, contract, canEdit, onChanged }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const docs = contract.documents || [];

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('File exceeds 25 MB limit'); return; }
    setUploading(true);
    try {
      await uploadDocument({ accountId, id: contract.id, file });
      toast.success('Document uploaded');
      onChanged?.();
    } catch (e2) {
      toast.error('Upload failed', { description: e2?.response?.data?.detail || e2.message });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const download = async (doc) => {
    try {
      const { url } = await getDocumentDownloadUrl({ accountId, id: contract.id, docId: doc.id });
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Download failed', { description: e?.response?.data?.detail || e.message });
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.filename}"?`)) return;
    try {
      await deleteDocument({ accountId, id: contract.id, docId: doc.id });
      toast.success('Document deleted');
      onChanged?.();
    } catch (e) {
      toast.error('Delete failed', { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold"><FileText className="w-4 h-4" /> Contract Documents</div>
        {canEdit && (
          <>
            <input ref={fileRef} type="file" className="hidden" onChange={onPick} data-testid="doc-file-input" />
            <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="doc-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
              {uploading ? 'Uploading…' : 'Upload Document'}
            </Button>
          </>
        )}
      </div>

      {docs.length === 0 ? (
        <div className="border border-dashed rounded-md p-6 text-center text-sm text-muted-foreground" data-testid="docs-empty">
          No documents uploaded yet.
        </div>
      ) : (
        <div className="border rounded-md divide-y">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between p-3" data-testid={`doc-row-${d.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{d.filename}</div>
                  <div className="text-xs text-muted-foreground">{fmtSize(d.size)} · {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : ''}{d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ''}</div>
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="ghost" onClick={() => download(d)} title="Download" data-testid={`doc-download-${d.id}`}><Download className="w-4 h-4" /></Button>
                {canEdit && <Button size="sm" variant="ghost" onClick={() => remove(d)} title="Delete" data-testid={`doc-delete-${d.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default DocumentsSection;
